import React, {
  SetStateAction,
  Dispatch,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Icon } from 'components/ui/icon';
import { cn } from 'lib/utils';
import BannerContainer, { BannerType } from 'components/banner';
import AmountDetails from 'components/amount-details';
import CURRENCY from 'utils/currency';
import { ITableData } from 'views/transactions/dispensaryList/details';
import { gatewatTransactionStatusFormatter } from 'utils/status-formatter';
import { formatToTz } from 'utils/date-format';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import STRING from 'utils/string';
import FormLabel from 'components/form-label';
import { Controller, useForm } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import usePayment from 'state/payment/hooks/usePayment';
import ErrorMessage from 'components/errorMessage';
import { GTW_STATUS } from 'utils/constants';

interface IProps {
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  refreshTnx: boolean;
  setRefreshTnx: Dispatch<SetStateAction<boolean>>;
  info: ITableData;
}

const FIELD_LABEL_CLASS = 'text-sm font-medium leading-5 text-gray-blue-80';
const USER_INFO_CLASS =
  'text-sm font-normal leading-5 text-gray-blue-70 mb-1';
const DETAIL_KEY_CLASS =
  'inline-block min-w-36 text-sm font-normal leading-5 text-gray-blue-60';

const PaymentAdjustment = ({
  showModal,
  setShowModal,
  refreshTnx,
  setRefreshTnx,
  info,
}: IProps): JSX.Element => {
  const { organisation } = useOrganisation();
  const { settings } = organisation;

  const { payment, paymentAdjustment, resetPaymentAdjustment } = usePayment();

  const { setValue, getValues, control } = useForm();

  const [showBanner, setShowBanner] = useState(true);

  const [showDetails, setShowDetails] = useState(false);

  const treezTicketID = STRING.getTreezTicketIDFromReferenceNumber(
    info.reference_number
  );

  const formatValue = useCallback((value: string) => {
    return `$${(Number(value) / 100).toFixed(2)}`;
  }, []);

  const getTotalFeeValue = (
    fee: number | undefined,
    tip: number | undefined,
    cashback: number | undefined
  ) => {
    const total_gross =
      (info.order_amount || getValues('gross_amount')) +
      (tip || 0) +
      (cashback || 0);

    const gross_fee = total_gross + (fee || 0);

    setValue('gross_amount', total_gross);
    setValue('total_gross_convenience', gross_fee);
  };

  const capturePaymentAdjustment = () => {
    const tipAmount = getValues('tip');
    const convenience_fee = getValues('convenience_fee');
    const gross_amount = info.order_amount;
    const cashbackAmount = getValues('cashback');

    const paymentId = info.gateway_id;

    if (paymentId) {
      paymentAdjustment({
        tipAmount: tipAmount ?? undefined,
        cashbackAmount: cashbackAmount ?? undefined,
        feeAmount: convenience_fee ?? undefined,
        originalAmount: gross_amount,
        paymentId: paymentId,
      });
    }
  };

  useEffect(() => {
    if (payment.capturePaymentAdjustment === true) {
      resetPaymentAdjustment();
      setShowModal(false);
      setTimeout(() => {
        setRefreshTnx(true);
      }, 12000);
    }
  }, [payment.capturePaymentAdjustment]);

  const errorMessage = payment.error?.paymentAdjustmentError?.message;

  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record Sale as Successful</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {info.adjusted_status !== GTW_STATUS.APPROVED && (
            <BannerContainer
              message={
                'The payment hasn’t been recorded yet and remains in a pending state. Please continue below to finalize and record this sale as successful.'
              }
              showBanner={showBanner}
              setShowBanner={() => setShowBanner(!showBanner)}
              type={BannerType.info}
              listOfError={false}
              fixedHeight={false}
              extraLink="https://www.google.com"
            />
          )}

          <AmountDetails
            amount={CURRENCY.getGrossAmount(
              info.gross_receipt_paid_calculated,
              info.gross_receipt_paid,
              info.grossAmount
            )}
            unit="USD"
            statusBadge={gatewatTransactionStatusFormatter(
              info.adjusted_status ?? info.gateway_status
            )}
          />

          <hr className="my-0" />

          <div className="flex items-center gap-10">
            <div>
              <div className="text-sm font-medium text-gray-blue-60">Date</div>
              <div className="text-sm text-gray-blue-80">
                {info.create_date
                  ? formatToTz(
                      info.create_date,
                      settings?.timezone,
                      'MMM dd, hh:mm a'
                    )
                  : 'NA'}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-blue-60">
                Payment method
              </div>
              <div className="text-sm text-gray-blue-80">{info.method}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-blue-60">
                Ticket ID
              </div>
              <div className="text-sm text-gray-blue-80">{treezTicketID}</div>
            </div>
          </div>

          <hr className="my-0" />

          <span className={USER_INFO_CLASS}>
            Please enter any tip, cashback, or additional fees and record this
            sale as successful.
          </span>

          <div className="flex flex-wrap gap-3">
            <div className="w-56">
              <FormLabel
                check
                element="tip"
                label="Tip"
                className={FIELD_LABEL_CLASS}
              />
              <Controller
                name="tip"
                control={control}
                rules={{ required: true }}
                defaultValue={info.tip_amount || 0}
                render={({ field }) => (
                  <NumberFormat
                    customInput={Input}
                    getInputRef={field.ref}
                    name={field.name}
                    onBlur={field.onBlur}
                    id="tip"
                    data-testid="tip"
                    prefix="$"
                    fixedDecimalScale
                    decimalScale={2}
                    placeholder="$1.50"
                    format={formatValue}
                    value={field.value}
                    onValueChange={(values) => {
                      getTotalFeeValue(
                        getValues('convenience_fee'),
                        values.floatValue,
                        getValues('cashback')
                      );
                      field.onChange(values.floatValue);
                    }}
                  />
                )}
              />
            </div>
            <div className="w-56">
              <FormLabel
                check
                element="convenience_fee"
                label="Convenience fee"
                className={FIELD_LABEL_CLASS}
              />
              <Controller
                name="convenience_fee"
                control={control}
                rules={{ required: true }}
                defaultValue={info.order_fee_amount || 0}
                render={({ field }) => (
                  <NumberFormat
                    customInput={Input}
                    getInputRef={field.ref}
                    name={field.name}
                    onBlur={field.onBlur}
                    id="convenience_fee"
                    data-testid="convenience_fee"
                    prefix="$"
                    fixedDecimalScale
                    decimalScale={2}
                    placeholder="$1.50"
                    format={formatValue}
                    value={field.value}
                    onValueChange={(values) => {
                      getTotalFeeValue(
                        values.floatValue,
                        getValues('tip'),
                        getValues('cashback')
                      );
                      field.onChange(values.floatValue);
                    }}
                  />
                )}
              />
            </div>
          </div>

          <div className="w-56">
            <FormLabel
              check
              element="cashback"
              label="Cashback amount"
              className={FIELD_LABEL_CLASS}
            />
            <Controller
              name="cashback"
              control={control}
              rules={{ required: true }}
              defaultValue={info.cashback || 0}
              render={({ field }) => (
                <NumberFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  name={field.name}
                  onBlur={field.onBlur}
                  id="cashback"
                  data-testid="cashback"
                  prefix="$"
                  fixedDecimalScale
                  decimalScale={2}
                  placeholder="$1.50"
                  format={formatValue}
                  value={field.value}
                  onValueChange={(values) => {
                    getTotalFeeValue(
                      getValues('convenience_fee'),
                      getValues('tip'),
                      values.floatValue
                    );
                    field.onChange(values.floatValue);
                  }}
                />
              )}
            />
          </div>

          <hr className="my-0" />

          <div className="flex flex-wrap gap-3">
            <div className="w-56">
              <FormLabel
                check
                element="gross_amount"
                label="Total Gross Amount"
                className={FIELD_LABEL_CLASS}
              />
              <Controller
                name="gross_amount"
                control={control}
                rules={{ required: true }}
                defaultValue={info.order_amount || 0}
                render={({ field }) => (
                  <NumberFormat
                    customInput={Input}
                    getInputRef={field.ref}
                    name={field.name}
                    disabled
                    onBlur={field.onBlur}
                    id="gross_amount"
                    data-testid="gross_amount"
                    prefix="$"
                    fixedDecimalScale
                    decimalScale={2}
                    placeholder="$1.50"
                    format={formatValue}
                    value={field.value}
                    onValueChange={(values) => {
                      field.onChange(values.floatValue);
                    }}
                  />
                )}
              />
            </div>
            <div className="w-56">
              <FormLabel
                check
                element="total_gross_convenience"
                label="Total + Convenience fee"
                className={FIELD_LABEL_CLASS}
              />
              <Controller
                name="total_gross_convenience"
                control={control}
                rules={{ required: true }}
                defaultValue={info.grossAmount + info.order_fee_amount || 0}
                render={({ field }) => (
                  <NumberFormat
                    customInput={Input}
                    getInputRef={field.ref}
                    name={field.name}
                    disabled
                    onBlur={field.onBlur}
                    id="total_gross_convenience"
                    data-testid="total_gross_convenience"
                    prefix="$"
                    fixedDecimalScale
                    decimalScale={2}
                    placeholder="$1.50"
                    format={formatValue}
                    value={field.value}
                    onValueChange={(values) => {
                      field.onChange(values.floatValue);
                    }}
                  />
                )}
              />
            </div>
          </div>

          <hr className="my-0" />

          <button
            type="button"
            className="flex w-full items-center justify-between text-left"
            onClick={() => setShowDetails(!showDetails)}
            aria-expanded={showDetails}
          >
            <span className="text-base font-semibold leading-6 text-gray-blue-80">
              More details
            </span>
            <Icon
              name={showDetails ? 'chevron-up' : 'chevron-down'}
              size={20}
              className="text-gray-blue-80"
              aria-hidden="true"
            />
          </button>

          {showDetails && (
            <>
              <div>
                <h3 className="text-sm font-semibold leading-5 text-gray-blue-80">
                  Payment details
                </h3>
                <hr className="my-0 mt-1" />
                <div className="mt-3 space-y-1">
                  <div>
                    <span className={DETAIL_KEY_CLASS}>Transaction Id</span>
                    <span className={USER_INFO_CLASS}>
                      {info.gateway_id || 'NA'}
                    </span>
                  </div>
                  <div>
                    <span className={DETAIL_KEY_CLASS}>Customer details</span>
                    <span className={USER_INFO_CLASS}>
                      {info.customerName || 'NA'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className={DETAIL_KEY_CLASS}></span>
                    <span
                      className={cn(
                        USER_INFO_CLASS,
                        'inline-flex items-center'
                      )}
                    >
                      <Icon name="phone" size={14} className="mr-1" aria-hidden="true" />
                      {info.customer_phone || 'NA'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className={DETAIL_KEY_CLASS}></span>
                    <span
                      className={cn(
                        USER_INFO_CLASS,
                        'inline-flex items-center'
                      )}
                    >
                      <Icon name="mail" size={14} className="mr-1" aria-hidden="true" />
                      {info.customer_email || 'NA'}
                    </span>
                  </div>
                  <div>
                    <span className={DETAIL_KEY_CLASS}>Payment Provider</span>
                    <span className={USER_INFO_CLASS}>
                      {info.processor_name || 'NA'}
                    </span>
                  </div>
                  <div>
                    <span className={DETAIL_KEY_CLASS}>Reference ID</span>
                    <span className={USER_INFO_CLASS}>
                      {info.reference_number || 'NA'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <h3 className="text-sm font-semibold leading-5 text-gray-blue-80">
                  Associate details
                </h3>
                <hr className="my-0 mt-1" />
                <div className="mt-3 space-y-1">
                  <div>
                    <span className={DETAIL_KEY_CLASS}>Name</span>
                    <span className={USER_INFO_CLASS}>
                      {`${info?.treez_employee_first_name || 'NA'} ${
                        info.treez_employee_last_name || 'NA'
                      }`}
                    </span>
                  </div>
                  <div>
                    <span className={DETAIL_KEY_CLASS}>Employee ID</span>
                    <span className={USER_INFO_CLASS}>
                      {info.treez_employee_id || 'NA'}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter
          className={cn(
            'flex',
            errorMessage
              ? 'flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between'
              : 'justify-end gap-2'
          )}
        >
          {errorMessage && (
            <ErrorMessage
              isValidation
              message={errorMessage}
              preIcon={
                <Icon
                  name="alert-circle"
                  size={16}
                  className="text-red-400 mr-1 shrink-0"
                  aria-hidden="true"
                />
              }
            />
          )}
          <div className="flex justify-end gap-2">
            <Button onClick={() => setShowModal(false)} variant="secondary">
              Cancel
            </Button>
            <Button onClick={capturePaymentAdjustment}>Confirm</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentAdjustment;
